import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, clearBearerToken, formatApiError, setBearerToken, setSessionExpiredHandler } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // Expected on first load when not signed in (401). Only log non-401s.
      if (e?.response?.status && e.response.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("auth/me failed:", e.response.status);
      }
      setUser(false);
      return null;
    }
  }, []);

  // Clear any stale in-memory bearer token on app startup so the very first
  // /auth/me request uses fresh cookies only (avoids replaying an expired
  // token captured before a server-side data wipe / lockout / session reset).
  useEffect(() => {
    clearBearerToken();
    fetchMe();
  }, [fetchMe]);

  // Wire the axios interceptor → AuthContext so any non-auth 401 mid-session
  // soft-clears the user. ProtectedRoute then naturally redirects to /login.
  // This avoids the bounce loop caused by an aggressive window.location redirect.
  useEffect(() => {
    setSessionExpiredHandler(() => setUser(false));
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) {
        setBearerToken(data.access_token);
      }
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      // Clear any stale bearer token from a previous session so we don't
      // accidentally send an expired Authorization header on the next try.
      clearBearerToken();
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let error;
      if (detail) {
        error = formatApiError(detail);
      } else if (e?.code === "ERR_NETWORK" || !e?.response) {
        error =
          "Couldn't reach the server. Check your connection and try again.";
      } else {
        error = e?.message || `Login failed (status ${status || "unknown"}).`;
      }
      return { ok: false, status, error };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("logout request failed:", e?.response?.status || e?.message);
    }
    clearBearerToken();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, refresh: fetchMe }),
    [user, login, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
